import { TCreateJobReqDto, TCreateJobResDto } from "@/api/services/main/job/dtos/create-job-req.dto";
import { TJobRowExcel } from "@/features/job/types/job-excel";
import { TGetJobListReqDto, TGetJobListResDto, TGetJobFilterOptionsResDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { TGetJobIdsWithPagesReqDto, TGetJobIdsWithPagesResDto } from "@/api/services/main/job/dtos/get-job-ids-with-pages.dto";
import { TGetJobResDto } from "@/api/services/main/job/dtos/get-job.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CACHED_TIME } from "@/constants/common";
import { baseURL, clientApi } from "@/utils/api";
import { authTokenManager } from "@/services/auth-token-manager";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useMemo } from "react";

const processDataBeforeCallApi = (payload: TCreateJobReqDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return formData.append(key, "");
    if (Array.isArray(value) && value.length === 0) return formData.append(key, "");

    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

export const createJobApi = async (payload: TCreateJobReqDto): Promise<TCreateJobResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateJobResDto>("/jobs", formData);
  return rs.data;
};

export const fetchJobFilterOptionsApi = async (params?: { companyId?: number }): Promise<TGetJobFilterOptionsResDto> => {
  const { data } = await clientApi.get<TGetJobFilterOptionsResDto>("/jobs/filter-options", { params });
  return data;
};

export const useJobFilterOptions = (params?: { companyId?: number }) => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_FILTER_OPTIONS, params?.companyId ?? null],
    queryFn: () => fetchJobFilterOptionsApi(params),
    staleTime: CACHED_TIME,
  });

  const managementNumberOptions = useMemo(() => (data?.data?.managementNumbers ?? []).map((v) => ({ label: v, value: v })), [data]);
  const jobNameOptions = useMemo(() => (data?.data?.jobNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const workplaceOptions = useMemo(() => (data?.data?.workplaces ?? []).map((v) => ({ label: v, value: v })), [data]);
  const companyNameOptions = useMemo(() => (data?.data?.companyNames ?? []).map((v) => ({ label: v, value: v })), [data]);

  return { managementNumberOptions, jobNameOptions, workplaceOptions, companyNameOptions, ...rest };
};

export const getJobListApi = async (params: TGetJobListReqDto): Promise<TGetJobListResDto> => {
  const { data } = await clientApi.get<TGetJobListResDto>("/jobs", { params });
  return data;
};

export const useJobList = (params: TGetJobListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_LIST, params],
    queryFn: () => getJobListApi(params),
  });
};

export const fetchJobInfoApi = async (id: string | number): Promise<TGetJobResDto> => {
  const { data } = await clientApi.get<TGetJobResDto>(`/jobs/${id}`);
  return data;
};

export const useJobInfo = (id?: number) => {
  const { jobId } = useParams();

  const fetchId = id ?? jobId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchJobInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const updateJobApi = async (id: string | number, payload: TCreateJobReqDto): Promise<TCreateJobResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.put<TCreateJobResDto>(`/jobs/${id}`, formData);
  return rs.data;
};

export const deleteJobsApi = async (jobIds: number[]): Promise<{ data: { deletedCount: number }; message?: string }> => {
  const { data } = await clientApi.delete("/jobs", { data: { jobIds } });
  return data;
};

export const checkDeletableJobsApi = async (jobIds: number[]): Promise<{ data: Record<number, boolean> }> => {
  const { data } = await clientApi.post<{ data: Record<number, boolean> }>("/jobs/check-deletable", { jobIds });
  return data;
};

export type TExportJobDocxParams = {
  /** "full" → output all fields. "partial" → blank out 5 internal-only fields (紹介料 売上/仕入, 求人獲得ルート, 顧客区分, 求人担当者コメント). */
  mask?: "full" | "partial";
  /** "none" → JA-only output (current default). Other ISO-ish codes → multilang docx with AI-translated values. */
  lang?: "none" | "vi" | "id" | "my" | "en" | "ne";
};

export const exportJobDocxApi = async (jobId: string | number, params: TExportJobDocxParams = {}): Promise<Blob> => {
  const { data } = await clientApi.get(`/jobs/${jobId}/export-docx`, {
    responseType: "blob",
    params,
  });
  return data;
};

// Streaming response type for AI scan progress
export type TGenerateScanJobStreamChunk = {
  progress?: number;
  data?: unknown;
  error?: string;
};

// Generate scan job API with streaming progress support
export const generateScanJobDescriptionApi = async (
  file: File,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<{ data: unknown }> => {
  const makeFetchRequest = async (authToken: string | null) => {
    const formData = new FormData();
    formData.append("file", file);

    return await fetch(`${baseURL}/jobs/generate-scan-image`, {
      method: "POST",
      headers: {
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: formData,
      signal,
    });
  };

  const processStreamingResponse = async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("ReadableStream not supported");
    }

    const decoder = new TextDecoder();
    let resultData: unknown = null;
    let buffer = "";

    // Extract complete JSON objects from buffer by tracking brace depth
    const extractJsonObjects = (input: string): { objects: string[]; remaining: string } => {
      const objects: string[] = [];
      let depth = 0;
      let start = -1;
      let inString = false;
      let escapeNext = false;

      for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === "\\" && inString) {
          escapeNext = true;
          continue;
        }

        if (char === '"' && !escapeNext) {
          inString = !inString;
          continue;
        }

        if (inString) continue;

        if (char === "{") {
          if (depth === 0) start = i;
          depth++;
        } else if (char === "}") {
          depth--;
          if (depth === 0 && start !== -1) {
            objects.push(input.slice(start, i + 1));
            start = -1;
          }
        }
      }

      // Return remaining incomplete JSON
      const remaining = start !== -1 ? input.slice(start) : "";
      return { objects, remaining };
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const { objects, remaining } = extractJsonObjects(buffer);
      buffer = remaining;

      for (const jsonStr of objects) {
        try {
          const parsed: TGenerateScanJobStreamChunk = JSON.parse(jsonStr);

          if (parsed.progress !== undefined && onProgress) {
            onProgress(parsed.progress);
          }

          if (parsed.data !== undefined) {
            resultData = parsed.data;
          }

          if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }

    return { data: resultData };
  };

  const authToken = authTokenManager.getToken();
  let response = await makeFetchRequest(authToken);

  if (response.status === 401) {
    const newToken = await authTokenManager.refreshToken();
    if (!newToken) {
      throw new Error("Authentication failed. Please login again.");
    }
    response = await makeFetchRequest(newToken);
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  return await processStreamingResponse(response);
};

export const validateImportJobApi = async (payload: TJobRowExcel[]) => {
  const rs = await clientApi.post("/jobs/validate-import", payload);
  return rs.data;
};

export const importJobsApi = async (payload: unknown[]) => {
  const rs = await clientApi.post("/jobs/import", payload);
  return rs.data;
};

export const getJobIdsWithPagesApi = async (payload: TGetJobIdsWithPagesReqDto): Promise<TGetJobIdsWithPagesResDto> => {
  const { data } = await clientApi.post<TGetJobIdsWithPagesResDto>("/jobs/ids-with-pages", payload);
  return data;
};
